import { logger } from '@adornis/base/logging.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { PDFFile } from '@adornis/pdf/db/pdf-file.js';
import { unlink } from 'node:fs/promises';
import { setReleaseFileReaders } from '../api/distribute-exam-results.js';
import { cancelLiveJobsForExam } from '../api/jobs/job-store.js';
import { EXAM_JOBS_COLLECTION } from '../api/jobs/job.js';
import { NOTIFICATIONS_COLLECTION } from '../api/result-notifications.js';
import { can } from '../authz/access.js';
import { Conversation } from '../conversation.js';
import { CopyDetectionResultRecord } from '../copy-detection-result.js';
import { Exam } from '../exam.js';
import { ExamResultRelease } from '../exam-result-release.js';
import { Feedback } from '../feedback.js';
import { Submission } from '../submission.js';
import { collectStrings } from './collect-strings.js';
import { TRANSCRIPT_COLLECTION } from './copy-detection-answers.js';

/**
 * Deleting an exam or a submission removes everything that hangs on it — student scans, answers, grades, released
 * results, job payloads with real names — not just the document the teacher clicked (DSGVO Art. 5(1)(e), Art. 17).
 * Server only: the delete mutations import this module dynamically, so `node:fs` and the job store stay out of the
 * client bundle.
 *
 * FILES HAVE NO BACK-REFERENCE to the exam, so they are found through the ids the exam's documents mention. Every
 * string in those documents is a candidate (see `collectStrings`) rather than a list of known file fields: a new
 * field that stores a file id is covered without anyone remembering to add it here.
 *
 * ONLY FILES THE CALLER MAY DELETE ARE DELETED. Attaching a file to an exam or submission only checks READ access
 * (update-exam.ts, create-submission.ts, pending-file.ts), so a student could attach a teacher's released
 * scan to their own practice exam; deleting that exam must not take the teacher's file with it. The file policy
 * (authz/policies.ts `fileRule`) allows delete to the owner only. Files from before owner stamping have no owner and
 * stay — they are counted in the log, not deleted.
 *
 * NOT COVERED: a job that is RUNNING at delete time cannot be stopped from outside (job-store.ts
 * `cancelLiveJobsForExam`), so it may still write files or rows after the exam is gone, and its own `exam_jobs` row
 * stays. Files no document refers to any more (consumed upload originals, older compiles, downloaded exports) cannot
 * be found here either.
 */

const FILE_CLASSES: Record<string, typeof AdornisFile> = {
  [AdornisFile._class]: AdornisFile,
  [ImageFile._class]: ImageFile,
  [PDFFile._class]: PDFFile,
};

/**
 * Deletes a stored file through ITS OWN class. file-utils builds the storage path from the class it is called on
 * (`getFullPath` uses `this._class`), not from the document's `_class` — so `AdornisFile.removeFile` on a PDF looks
 * under `File/` instead of `PDFFile/`, the unlink throws before the document is removed, and the file silently stays
 * on disk and in the database.
 *
 * Tolerant where `removeFile` is not: a file already gone from disk still has its document removed, and a missing
 * document is not an error. Otherwise a second delete attempt after a partial failure would stop at the first file
 * it had already removed, and a document without its file would stay forever.
 */
export async function removeStoredFile(fileId: string): Promise<boolean> {
  const files = await getRawCollection(AdornisFile._collectionName);
  const stored = await files.findOne({ _id: fileId as any }, { projection: { _id: 1, _class: 1, meta: 1 } });
  if (!stored) return false;
  const FileClass = FILE_CLASSES[String(stored._class)] ?? AdornisFile;
  if (stored.meta?.extension) {
    await unlink(FileClass.getFullPath(stored as any)).catch((err: any) => {
      if (err?.code !== 'ENOENT') throw err;
    });
  }
  await files.deleteOne({ _id: fileId as any });
  return true;
}

/**
 * Deletes those of `candidateIds` that are files the caller may delete — checked through the caller's request, or,
 * with `ownerUserID`, against that owner. A background job has no caller, and in server context every policy
 * allows (ServerContextPolicy), so it must name whose files may go.
 */
async function deleteOwnFiles(
  candidateIds: Iterable<string>,
  ownerUserID?: string,
): Promise<{ deleted: number; skipped: number }> {
  const ids = [...new Set(candidateIds)];
  if (!ids.length) return { deleted: 0, skipped: 0 };
  const docs = await (await getRawCollection(AdornisFile._collectionName))
    .find({ _id: { $in: ids as any[] } }, { projection: { _id: 1, _class: 1, ownerUserID: 1, readerUserIDs: 1 } })
    .toArray();

  let deleted = 0;
  let skipped = 0;
  for (const doc of docs) {
    const mayDelete = ownerUserID
      ? doc.ownerUserID === ownerUserID
      : await can(doc._class ?? AdornisFile._class, 'delete', doc);
    if (!mayDelete) {
      skipped += 1;
      continue;
    }
    await removeStoredFile(String(doc._id));
    deleted += 1;
  }
  return { deleted, skipped };
}

/**
 * Deletes an exam and everything that hangs on it. The caller has already asserted `delete` on the exam.
 *
 * `keepReleases`: the results released to students stay, with their attached files and the students' read access.
 * A release is a frozen, self-contained snapshot (title, grade, task texts, answers, feedback — see
 * exam-result-release.ts) and the student views read nothing else (get-my-class-exam-results.ts), so the students
 * keep their full result without the exam. Kept releases still go with their class (delete-classroom.ts).
 *
 * `ownerUserID`: for a caller without a request (server/guest-retention.ts) — only that owner's files are deleted.
 *
 * The exam goes LAST: until then it is what every step finds the rest through, so a failure part-way leaves it in
 * place and deleting it again finishes the job.
 */
export async function deleteExamCascade(
  examId: string,
  { keepReleases = false, ownerUserID }: { keepReleases?: boolean; ownerUserID?: string } = {},
): Promise<void> {
  const exams = await getRawCollection(Exam._collectionName);
  const exam = await exams.findOne({ _id: examId as any });
  if (!exam) return;

  const conversationId = typeof exam.conversationId === 'string' && exam.conversationId ? exam.conversationId : null;
  const conversations = await getRawCollection(Conversation._collectionName);
  const conversation = conversationId ? await conversations.findOne({ _id: conversationId as any }) : null;
  const submissions = await (await getRawCollection(Submission._collectionName)).find({ examId }).toArray();
  const releasesCollection = await getRawCollection(ExamResultRelease._collectionName);
  const releases = await releasesCollection.find({ examId }).toArray();

  // Pending work only; a running job finishes on its own (see this module's header).
  const { cancelled, stillClaimed } = await cancelLiveJobsForExam(examId, 'exam-deleted');

  // Students lose read access first, so a released file that is not the caller's to delete is not left readable.
  if (!keepReleases) await setReleaseFileReaders(releases, false);
  const keptFileIds = new Set<string>(
    keepReleases
      ? releases.flatMap(release => [...(release.attachedImageIds ?? []), release.attachedPdfId]).filter(Boolean)
      : [],
  );

  const referencedIds = collectStrings([exam, conversation, submissions, releases]);
  const files = await deleteOwnFiles(
    [...referencedIds].filter(id => !keptFileIds.has(id)),
    ownerUserID,
  );

  // Transcripts are keyed by the crop image id, not the exam — the referenced ids are where those crops are named.
  await (await getRawCollection(TRANSCRIPT_COLLECTION)).deleteMany({ _id: { $in: [...referencedIds] as any[] } });
  await (await getRawCollection(CopyDetectionResultRecord._collectionName)).deleteMany({ examId });
  if (!keepReleases) await releasesCollection.deleteMany({ examId });
  await (await getRawCollection(NOTIFICATIONS_COLLECTION)).deleteMany({ examId });
  // A claimed row belongs to a running worker; removing it would pull the row out from under its ClaimGuard.
  const jobs = await (await getRawCollection(EXAM_JOBS_COLLECTION)).deleteMany({ examId, status: { $ne: 'claimed' } });
  const deletedSubmissions = await (await getRawCollection(Submission._collectionName)).deleteMany({ examId });
  if (conversationId) {
    await (await getRawCollection(Feedback._collectionName)).deleteMany({ conversationId });
    await conversations.deleteOne({ _id: conversationId as any });
  }
  await exams.deleteOne({ _id: examId as any });

  logger.info(
    {
      context: 'deleteExamCascade',
      examId,
      submissions: deletedSubmissions.deletedCount,
      releases: releases.length,
      releasesKept: keepReleases,
      filesDeleted: files.deleted,
      filesSkipped: files.skipped,
      jobsCancelled: cancelled,
      jobsDeleted: jobs.deletedCount,
      jobsStillRunning: stillClaimed,
    },
    'Exam deleted with its data',
  );
}

/**
 * Deletes a submission, its files and the results released from it. The caller has already asserted `delete` on it.
 *
 * Files the exam or another submission still mentions stay: without an uploaded blank exam, `blankExamIds` are the
 * first submission's pages (pipeline/structure-step.ts), and pages can have been moved between submissions in the
 * split review.
 */
export async function deleteSubmissionCascade(submissionId: string): Promise<void> {
  const submissionsCollection = await getRawCollection(Submission._collectionName);
  const submission = await submissionsCollection.findOne({ _id: submissionId as any });
  if (!submission) return;

  const examId = submission.examId;
  const exam = examId ? await (await getRawCollection(Exam._collectionName)).findOne({ _id: examId }) : null;
  const otherSubmissions = examId
    ? await submissionsCollection.find({ examId, _id: { $ne: submissionId as any } }).toArray()
    : [];
  const releasesCollection = await getRawCollection(ExamResultRelease._collectionName);
  const releases = await releasesCollection.find({ submissionId }).toArray();

  await setReleaseFileReaders(releases, false);

  const stillReferenced = collectStrings([exam, otherSubmissions]);
  const ownIds = [...collectStrings([submission, releases])].filter(id => !stillReferenced.has(id));
  const files = await deleteOwnFiles(ownIds);

  await (await getRawCollection(TRANSCRIPT_COLLECTION)).deleteMany({ _id: { $in: ownIds as any[] } });
  await releasesCollection.deleteMany({ submissionId });
  await submissionsCollection.deleteOne({ _id: submissionId as any });

  logger.info(
    {
      context: 'deleteSubmissionCascade',
      submissionId,
      examId,
      releases: releases.length,
      filesDeleted: files.deleted,
      filesSkipped: files.skipped,
    },
    'Submission deleted with its data',
  );
}
